import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import {
  buildResponseValidationReport,
  camelcaseKeysFromApi,
  type KyRequestOptions,
} from '@vestigas/data';

import { vestigasApiNoConversion } from '~/services/kyClient';

import { log } from '~/utils';

import { type DeleteDeliveryNotesAdminPayload } from './types';
import {
  zodDeleteDeliveryNotesAdminPayloadSchema,
  zodDeleteDeliveryNotesAdminResponseSchema,
} from './validations';

/**
 * Permanently deletes delivery notes of any company account from Couchbase, Postgres and the
 * Redis cache, including their chains, shares and attachments.
 *
 * @param deliveryNoteIds - The IDs of the delivery notes (asset mains) to delete.
 * @param options - Additional request options.
 * @returns What was removed per store, plus the Postgres counts per ID in request order; all zero
 * for an ID that matched no delivery note. `null` when
 * the API confirmed the deletion with a 2xx but its answer could not be read: the deletion has
 * run by then, so an unreadable answer must not surface as a failed deletion.
 * @throws An `HTTPError` for a non-2xx answer, and the request error when no answer arrived.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/delete_dlns_route_asset_delivery_note_admin_delete_dlns_delete
 */
export async function deleteDeliveryNotesAdmin(
  deliveryNoteIds: readonly UUID[],
  options?: KyRequestOptions,
) {
  const payload: DeleteDeliveryNotesAdminPayload = {
    asset_ids: [...deliveryNoteIds],
  };

  // The body is read here rather than by the client, so a confirmed deletion whose answer cannot
  // be read is told apart from a failed request.
  const response = await vestigasApiNoConversion.delete(
    ENDPOINT.DELIVERY.DELETE_ADMIN(),
    {
      json: payload,
      ...options,
      validate: {
        ...options?.validate,
        body: zodDeleteDeliveryNotesAdminPayloadSchema,
      },
    },
  );

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return null;
  }

  const parsed = zodDeleteDeliveryNotesAdminResponseSchema.safeParse(
    camelcaseKeysFromApi(body),
  );

  if (!parsed.success) {
    log.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key: 'delete-delivery-note-admin',
      message: 'delete delivery note admin response has validation errors',
    });

    return null;
  }

  return parsed.data;
}
